<template>
  <form novalidate @submit.prevent="whenValid(onSubmit)">
    <h3 class="headline my-4 blue--text text--darken-4">
      Please provide the following data for your property.
    </h3>
    <v-text-field
      v-model.trim="form.name"
      v-validate="'required|max:50|alpha_spaces'"
      :counter="50"
      :error-messages="errors.collect('name')"
      label="Name"
      data-vv-name="name"
      autofocus
      required
    >
    </v-text-field>
    <v-text-field
      v-model.trim="form.email"
      v-validate="'required|email'"
      :error-messages="errors.collect('email')"
      label="E-mail"
      type="email"
      data-vv-name="email"
      required
    >
    </v-text-field>
    <v-text-field
      v-model="form.phone"
      v-validate="'required'"
      :error-messages="errors.collect('phone')"
      label="Phone Number"
      mask="phone"
      data-vv-name="phone"
      required
    ></v-text-field>
    <v-container grid-list-xl px-0>
      <v-layout row wrap>
        <v-flex xs12 mt-4 mb-0 pb-0>
          <h2 class="subheading blue--text text--darken-4">Property Address</h2>
        </v-flex>
        <v-flex>
          <v-text-field
            v-model.trim="form.address"
            v-validate="'required'"
            :error-messages="errors.collect('address')"
            label="Property Address"
            placeholder="12345 Las Vegas Blvd Las Vegas NV 89118"
            clearable
            data-vv-name="address"
            required
          ></v-text-field>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container grid-list-xl px-0>
      <v-layout row wrap>
        <v-flex xs12>
          <h2 class="subheading blue--text text--darken-4">Property Facts</h2>
        </v-flex>
        <v-flex xs6 sm3 d-flex>
          <v-select
            v-model="form.size"
            :items="sizes"
            label="Square ft"
            outline
          >
          </v-select>
        </v-flex>
        <v-flex xs6 sm3 d-flex>
          <v-select
            v-model="form.acres"
            :items="acres"
            label="Lot Size"
            outline
          ></v-select>
        </v-flex>
        <v-flex xs6 sm3 d-flex>
          <v-select
            v-model="form.year"
            :items="years"
            label="Year Built"
            outline
          >
          </v-select>
        </v-flex>
        <v-flex xs6 sm3 d-flex>
          <v-select v-model="form.noHOA" :items="hoa" label="HOA Fees" outline>
          </v-select>
        </v-flex>
      </v-layout>
      <v-layout row wrap>
        <v-flex xs12 mt-4>
          <h2 class="subheading blue--text text--darken-4">
            Bathrooms and Bathrooms
          </h2>
        </v-flex>
        <v-flex xs6 sm3 d-flex>
          <v-select
            v-model="form.rooms"
            :items="rooms"
            label="Bedrooms"
            outline
          >
          </v-select>
        </v-flex>
        <v-flex xs6 sm3 d-flex>
          <v-select
            v-model="form.baths"
            :items="baths"
            label="Bathrooms"
            outline
          >
          </v-select>
        </v-flex>
        <v-flex xs6 sm3 d-flex>
          <v-select
            v-model="form.garages"
            :items="garages"
            label="Garages"
            outline
          >
          </v-select>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container grid-list-xl px-0>
      <v-layout row wrap>
        <v-flex xs12 mt-3 mb-0 pb-0>
          <h2 class="subheading blue--text text--darken-4">
            Additional Features
          </h2>
        </v-flex>
        <v-flex v-for="(option, index) in options" :key="index" xs12 sm6>
          <v-switch
            v-model="form.features[option.name]"
            :label="switchLabel(option.text)"
            color="primary"
          ></v-switch>
        </v-flex>
      </v-layout>
    </v-container>
    <v-layout row wrap>
      <v-flex xs12 mt-4 mb-2>
        <h2 class="subheading blue--text text--darken-4">
          Additional Information
        </h2>
      </v-flex>
      <v-flex>
        <v-textarea
          v-model.trim="form.message"
          v-validate="'max:250'"
          :counter="250"
          :error-messages="errors.collect('message')"
          auto-grow
          label="Message"
          data-vv-name="message"
          rows="1"
        ></v-textarea>
      </v-flex>
    </v-layout>
    <v-checkbox
      v-model="form.human"
      v-validate="'required'"
      :error-messages="errors.collect('human')"
      label="I am a real person"
      value="1"
      data-vv-name="human"
      type="checkbox"
      required
    >
    </v-checkbox>
    <v-layout row wrap>
      <v-flex class="text-xs-center mt-5">
        <v-btn
          :disabled="errors.any() || !isComplete"
          large
          color="success"
          type="submit"
        >
          submit
        </v-btn>
        <v-btn large @click="clear">
          clear
        </v-btn>
      </v-flex>
    </v-layout>
    <v-alert
      :value="contactError"
      color="error"
      icon="warning"
      outline
      class="mt-3"
    >
      {{ contactError }}
    </v-alert>
  </form>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex';
import validate from '../mixins/validate';
import { contact } from '../api/contact';
import { lowercase } from '../utils/filters';
import {
  rooms,
  baths,
  garages,
  options,
  sizes,
  acres,
  hoa,
  years,
} from '../json/search.json';

export default {
  name: 'SellerContactForm',
  mixins: [validate],
  $_veeValidate: {
    validator: 'new',
  },
  metaInfo: {
    title: 'Contact Us',
  },
  data() {
    return {
      form: {
        name: '',
        email: lowercase(''),
        phone: '',
        address: '',
        subject: 'Seller',
        message: '',
        human: null,
        year: '',
        acres: '',
        size: '',
        noHOA: '',
        rooms: '',
        baths: '',
        garages: '',
        features: {
          basement: false,
          casita: false,
          gated: false,
          guardGated: false,
          horses: false,
          pool: false,
          singleStory: false,
          spa: false,
          rv: false,
        },
      },
    };
  },
  error: null,
  dictionary: {
    attributes: {
      email: 'E-mail Address',
      phone: 'Phone Number',
    },
    custom: {
      name: {
        required: () => 'Name can not be empty',
        max: 'Name field may not be greater than 50 characters',
      },
      message: {
        required: () => 'Message can not be empty',
        min: 'A minimum of 3 characters are required',
      },
    },
  },
  computed: {
    ...mapGetters({
      contactError: 'contactError',
      user: 'user',
    }),
    isComplete() {
      return (
        this.form.name &&
        this.form.email &&
        this.form.phone &&
        this.form.address &&
        this.form.rooms &&
        this.form.baths &&
        this.form.garages &&
        this.form.human
      );
    },
  },
  created() {
    this.rooms = rooms;
    this.baths = baths;
    this.garages = garages;
    this.options = options;
    this.sizes = sizes;
    this.acres = acres;
    this.hoa = hoa;
    this.years = years;
  },
  mounted() {
    this.$validator.localize('en', this.dictionary);
  },
  methods: {
    ...mapMutations(['showSnackbar', 'closeSnackbar']),
    onSubmit() {
      this.$store.dispatch('contact');
      contact('contact-seller', this.$data.form)
        .then((res) => {
          this.$store.commit('contactSuccess', res);
          this.clear();
          this.contactSuccess();
        })
        .then(() => this.$router.push({ path: '/' }))
        .catch((error) => {
          this.$store.commit('contactFailed', { error });
          this.contactFailed();
        });
    },
    contactSuccess() {
      this.showSnackbar({
        text: 'You have successfully sent an email!',
        color: 'success',
        timeout: 3000,
      });
    },
    contactFailed() {
      this.showSnackbar({
        text: 'There were problems with your email message!',
        color: 'warning',
        timeout: 3000,
      });
    },
    clear() {
      this.form.name = '';
      this.form.email = '';
      this.form.phone = '';
      this.form.message = '';
      this.form.address = '';
      this.form.lot = '';
      this.form.size = '';
      this.form.hoa = '';
      this.form.year = '';
      this.form.baths = '';
      this.form.rooms = '';
      this.form.garages = '';
      this.form.human = false;
      this.$validator.reset();
    },
    switchLabel(label) {
      return label.toString();
    },
  },
};
</script>
